import { DISALLOWED_EMAILS, ERROR_MSGS, MAX_FILE_SIZE, MAX_PDF_SIZE, TLD_S3_PATH_CONFIG, TLDS } from '../constants'
import { PLANS_ID, PLANS, PLAN_THRESHOLDS, PLAN_ORDER } from '../constants/plans'
import JSZip from 'jszip'
import { FILE_TYPE_HTML, FILE_TYPE_PDF } from 'components/CreateUpdateSiteModal/interface'
import { setShowPaymentModal } from 'actions'
import {
  imageExtensions,
  excelExtensions,
  docExtensions,
  pptExtensions,
  videoExtensions,
  audioExtensionsBrowserNative,
  codeExtensions,
  lottieExtensions,
  ebookExtensions,
  latexExtensions,
  getSafeFileName
} from '../helpers/file-type'
import { isHtmlFile, cleanFileName } from '../helpers/file-type'
import { v4 as uuidv4 } from 'uuid'
import { uniqueNamesGenerator, colors, names, NumberDictionary } from 'unique-names-generator'

export function isPDF(file) {
  return file?.type === FILE_TYPE_PDF
}

export const isHtml = (file) => {
  return file?.type === FILE_TYPE_HTML || isHtmlFile(file?.name, file?.type)
}

export const isPhpFile = (file) => {
  return (
    file?.type?.toLowerCase().includes('php') ||
    file?.fileType?.toLowerCase().includes('php') ||
    file?.name?.toLowerCase().includes('php')
  )
}

export const isPythonFile = (file) => {
  const filename = typeof file === 'string' ? file : file?.name
  return (
    file?.type?.toLowerCase().includes('python') ||
    file?.fileType?.toLowerCase().includes('python') ||
    filename?.toLowerCase().endsWith('.py')
  )
}

export const isWasmerFile = (file) => {
  return isPhpFile(file) || isPythonFile(file) || isNodeOrReactZip(file)
}

export const isNodeOrReactZip = (file) => {
  return file?.isNodeOrReactZip === true
}

export const convertToBytes = (size) => 1024 * 1024 * size

export function formatMbToGb({ mb, pow = 1 }) {
  const k = 1024
  return Number(Number(mb / Math.pow(k, pow)).toFixed(4))
}

export function isValidFileSize(file, maxSize) {
  if (maxSize) {
    return file.size < convertToBytes(maxSize)
  } else if (file.type === FILE_TYPE_PDF) {
    return file.size < convertToBytes(MAX_PDF_SIZE)
  } else return file.size < convertToBytes(MAX_FILE_SIZE)
}

export function bytesToMB(bytes, useBinary = true) {
  const divisor = useBinary ? 1024 * 1024 : 1000000 // Use 1024^2 for binary or 10^6 for decimal
  return (bytes / divisor).toFixed(2) // Rounds to 2 decimal places
}

export function isTotalSizeWithinLimit(fileToAdd, existingFilesArray, limitMb) {
  const totalSize = existingFilesArray.reduce((accumulator, file) => accumulator + file.size, fileToAdd.size)
  const maxSize = limitMb || (fileToAdd.type === FILE_TYPE_PDF ? MAX_PDF_SIZE : MAX_FILE_SIZE)

  return totalSize < convertToBytes(maxSize)
}

export const pluralize = (singular, plural, count) => (count === 1 ? singular : plural)

export function formatNumber(n) {
  const nFormat = new Intl.NumberFormat()
  return nFormat.format(n)
}

export function formatStorage(sizeMB) {
  if (sizeMB >= 1000000) {
    const n = sizeMB / 1000000
    return `${n} TB`
  }
  if (sizeMB >= 1000) {
    const n = sizeMB / 1000
    return `${n} GB`
  }
  return `${sizeMB} MB`
}

export const copyToClipboard = (text) => {
  const el = document.createElement('textarea')
  el.value = text
  document.body.appendChild(el)
  try {
    el.select()
    document.execCommand('copy')
  } finally {
    if (el.parentNode) {
      el.parentNode.removeChild(el)
    }
  }
}

export const getDirectFileLink = (root, filename, ext, dateCreated) => {
  if (!filename) return null
  else return `${root}/${filename.replace(`.${ext}`, '')}-${dateCreated.replace(/:|\\./g, '-')}.${ext}`
}

export const validateDisallowedEmails = (email) =>
  !DISALLOWED_EMAILS.some((e) => email.match(`${e}$`)) && !email.includes('+')

export const getErrorMessage = (err) => {
  if (typeof err === 'string') return ERROR_MSGS[err]
  return ERROR_MSGS[err?.response?.data?.code || err?.code]
}

export function isDevSite() {
  return (
    window.location.hostname.includes('tiiny.co.uk') ||
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1'
  )
}

export function isPreviewSite() {
  return window.location.hostname.includes('preview.tiiny.co.uk')
}

export function isDevOrPreviewSite() {
  return isDevSite() || isPreviewSite()
}

export const mbValue = (mb) => 1024 * 1024 * mb

export const isTrialAccount = (userProfile) => !userProfile?.productId

export const isOnFreeTrial = (userProfile) => userProfile?.status === 'trialing'

export const checkTrialStatusLogic = async (userProfile) => {
  if (!userProfile?.email) return { hasUsedTrial: false, isLoading: false }
  if (userProfile.hasUsedTrial !== undefined) {
    return { hasUsedTrial: userProfile.hasUsedTrial, isLoading: false }
  }
}

export const isMac = () => navigator?.platform?.toUpperCase().indexOf('MAC') > -1

export function hashString(str = '') {
  let hash = 0,
    i,
    chr
  if (str?.length === 0) return hash
  for (i = 0; i < str.length; i++) {
    chr = str.charCodeAt(i)
    hash = (hash << 5) - hash + chr
    hash |= 0 // Convert to 32bit integer
  }
  return hash
}

export const isCustomDomain = (url) => !Object.values(TLDS).some((tld) => url.endsWith(tld))

export const getTLDs = ({ account }) => {
  if (isPreviewSite()) return [TLDS.TIINY_CO_UK]
  if (isDevSite()) return [TLDS.TIINY_CO_UK]
  return account?.validated ? [TLDS.TIINY_CO, TLDS.TIINY_SITE] : [TLDS.TIINY_SITE]
}

export const getSubdomainAndTLD = ({ site }) => {
  const { subdomain } = site
  let splitSubdomain = subdomain?.split('.')
  if (isPreviewSite()) {
    splitSubdomain = subdomain?.replace('preview', '')?.split('.')
  }
  return splitSubdomain ? { subdomain: splitSubdomain[0], tld: `.${splitSubdomain.slice(1).join('.')}` } : {}
}

export const getS3Path = ({ site, isCustomDomain }) => {
  const { subdomain, tld } = getSubdomainAndTLD({ site })
  return isCustomDomain ? `${tld.substring(1)}/${subdomain}` : TLD_S3_PATH_CONFIG[tld]({ subdomain, tld })
}

export const hasFeature = (planId, feature) => PLANS_ID[planId]?.features[feature]

export const isEligibleForAIEdit = (userProfile, site) => {
  const hasSubdomain = Boolean(site?.subdomain)
  const isEligiblePlan = !userProfile?.productId || ![PLANS.PRO_MAX.id, PLANS.API.id].includes(userProfile.productId)
  const isHTML =
    ['HTML', 'ZIP', 'EXAMPLE', 'text/html', 'application/zip'].includes(site?.type) && !site?.siteType?.includes('TEXT')
  const isPHP = site?.subdomain?.includes('tiiny.io')
  const isCollection = site?.siteType?.includes('multiple')

  return hasSubdomain && isEligiblePlan && !isPHP && isHTML && !isCollection
}

export const openAIEditor = (subdomain, isTrial) => {
  window.open(`/editor/?domain=${subdomain}&file=/index.html${isTrial ? '&trial=true' : ''}`, '_blank')
}

// Helper function to create a file input element
export const createFileInput = (acceptedFiles) => {
  const input = document.createElement('input')
  input.setAttribute('data-testid', 'FileSelectionForm-button-file-input')

  input.type = 'file'
  input.accept = acceptedFiles || '*/*'
  input.multiple = true

  return input
}

export async function zipMultipleFiles(addedFiles) {
  const zip = new JSZip()
  addedFiles.forEach((file) => {
    zip.file(file.path, file)
  })
  const content = await zip.generateAsync({ type: 'blob' })

  return new File([content], 'files.zip', {
    type: 'application/zip',
    lastModified: new Date()
  })
}

export const getFullDomainValue = (subdomain = '', domainSuffix = '') => {
  return subdomain + domainSuffix
}

export const getDomainSuffix = (subdomain) => {
  let splitSubdomain = subdomain?.split('.')
  if (isPreviewSite()) {
    splitSubdomain = subdomain?.replace('preview', '')?.split('.')
  }
  return subdomain ? { subdomain: splitSubdomain[0], tld: `.${splitSubdomain.slice(1).join('.')}` } : {}
}

export const convertToFormData = (formData, key, value) => {
  if (value !== null && value !== undefined) {
    formData.append(key, value)
  }
}

export const formDataToJson = (formData) => {
  const jsonObject = {}
  for (const [key, value] of formData.entries()) {
    jsonObject[key] = value
  }
  return jsonObject
}

export const isEmpty = (value) => {
  if (value === null || value === undefined) {
    return true
  }

  if (typeof value === 'string') {
    return value.trim() === ''
  }

  if (Array.isArray(value)) {
    return value.length === 0
  }

  if (typeof value === 'object') {
    return Object.keys(value).length === 0
  }

  return false
}

export const formatFileSize = (sizeInBytes) => {
  const sizeInKB = sizeInBytes / 1024
  if (sizeInKB < 1024) {
    return `${sizeInKB.toFixed(2)} KB`
  }

  const sizeInMB = sizeInKB / 1024
  if (sizeInMB < 1024) {
    return `${sizeInMB.toFixed(2)} MB`
  }

  const sizeInGB = sizeInMB / 1024
  return `${sizeInGB.toFixed(2)} GB`
}

export const isFileSizeGreaterThan3MB = (sizeInBytes) => {
  const megabyte = 1024 * 1024
  return sizeInBytes > 3 * megabyte
}

export const validateFilesSelection = (files, selectedFile) => {
  for (const file of files) {
    if (
      file?.path === selectedFile.path ||
      file?.name === selectedFile.name ||
      file?.s3FileName === selectedFile.name
    ) {
      return false
    }
  }
  return true
}

export const getDropzoneAcceptedFiles = (fileType) => {
  let acceptedFilesType = {
    'application/pdf': ['.pdf'],
    'text/html': ['.html', '.htm'],
    'application/zip': ['.zip'],
    'application/x-zip-compressed': ['.zip'],
    'text/php': ['.php'],
    image: imageExtensions.map((ext) => `.${ext}`),
    excel: excelExtensions.map((ext) => `.${ext}`),
    doc: docExtensions.map((ext) => `.${ext}`),
    ppt: pptExtensions.map((ext) => `.${ext}`),
    video: videoExtensions.map((ext) => `.${ext}`),
    audio: audioExtensionsBrowserNative.map((ext) => `.${ext}`),
    code: codeExtensions.map((ext) => `.${ext}`),
    lottie: lottieExtensions.map((ext) => `.${ext}`),
    ebook: ebookExtensions.map((ext) => `.${ext}`),
    latex: latexExtensions.map((ext) => `.${ext}`)
  }

  if (fileType && acceptedFilesType[fileType]) {
    return Object.values(acceptedFilesType[fileType]).flat()
  }

  return Object.values(acceptedFilesType).flat()
}

export const checkAccountAge = (created) => {
  const createdDate = new Date(created)
  const currentDate = new Date()

  // Calculate the difference in milliseconds
  const diffTime = Math.abs(currentDate - createdDate)
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

  // Check if the account is older than a day
  return diffDays > 1
}

export const isSameDate = (date1, date2) => {
  return (
    date1.getDate() === date2.getDate() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getFullYear() === date2.getFullYear()
  )
}

export const sortLoadingSites = (sites) => {
  const allSites = sites

  const mySites = allSites.reduce((acc, current) => {
    if (current?.linkStatus === 'processing') {
      const checker = acc.find(
        (site) => site.subdomain === current.subdomain && site?.linkStatus === current?.linkStatus
      )

      if (!checker) acc.push(current)
    }

    return acc
  }, [])

  return mySites
}

export const sortOnlineSites = (sites, sitesToAdd) => {
  const allSites = [...sitesToAdd, ...sites]

  const mySites = allSites.reduce((acc, current) => {
    if (current?.linkStatus === 'online' || current?.linkStatus === 'error' || !current?.linkStatus) {
      const checker = acc.find(
        (site) => site.subdomain === current.subdomain && site?.linkStatus === current?.linkStatus
      )

      if (!checker) acc.push(current)
    }

    return acc
  }, [])

  return mySites
}

export const sortDuplicateSites = (allSites, sitesToAdd) => {
  const sites = allSites.map((site) => {
    if (sitesToAdd?.length > 0) {
      for (let i = 0; i < sitesToAdd.length; i++) {
        if (site.subdomain === sitesToAdd[i].subdomain) {
          return sitesToAdd[i]
        }
      }
    }
    return site
  })

  return sites
}

export function isMoreThanOneDay(date) {
  const lastLogin = new Date(date)
  const now = new Date()
  const oneDayInMs = 24 * 60 * 60 * 1000 // Number of milliseconds in a day

  return now - lastLogin > oneDayInMs
}

export const getPlanIdByFeature = ({ fileSize, customDomains, projects, visitors, bandwidth }) => {
  if (
    fileSize > PLAN_THRESHOLDS.SOLO.fileSizeLimit ||
    (customDomains !== undefined && customDomains > PLAN_THRESHOLDS.SOLO.customDomainsLimit) ||
    (projects !== undefined && projects > PLAN_THRESHOLDS.SOLO.projectLimit) ||
    (visitors !== undefined && visitors > PLAN_THRESHOLDS.SOLO.visitorLimit) ||
    (bandwidth !== undefined && bandwidth > PLAN_THRESHOLDS.SOLO.bandwidthLimit)
  ) {
    return PLANS.PRO_U.id
  }

  if (
    fileSize > PLAN_THRESHOLDS.TINY.fileSizeLimit ||
    (customDomains !== undefined && customDomains > PLAN_THRESHOLDS.TINY.customDomainsLimit) ||
    (projects !== undefined && projects > PLAN_THRESHOLDS.TINY.projectLimit) ||
    (visitors !== undefined && visitors > PLAN_THRESHOLDS.TINY.visitorLimit) ||
    (bandwidth !== undefined && bandwidth > PLAN_THRESHOLDS.TINY.bandwidthLimit)
  ) {
    return PLANS.PRO.id
  }

  if (
    fileSize > PLAN_THRESHOLDS.FREE.fileSizeLimit ||
    (customDomains !== undefined && customDomains > PLAN_THRESHOLDS.FREE.customDomainsLimit) ||
    (projects !== undefined && projects > PLAN_THRESHOLDS.FREE.projectLimit) ||
    (visitors !== undefined && visitors > PLAN_THRESHOLDS.FREE.visitorLimit) ||
    (bandwidth !== undefined && bandwidth > PLAN_THRESHOLDS.FREE.bandwidthLimit)
  ) {
    return PLANS.TINY.id
  }

  return PLANS.TINY.id
}

export const onUpgradePlan = (payload = {}, dispatch) => {
  // gets the pricing plan id based on user action threshold
  let planId = payload.planId || getPlanIdByFeature(payload)

  // Override rule: Free users with teams ALWAYS go to PRO
  if ((!payload?.userProfile?.productId && payload?.userProfile?.teamSize > 0) || payload?.userProfile?.childAccount) {
    planId = PLANS.PRO_U.id
  }

  if (planId && payload?.userProfile?.productId && planId === payload.userProfile.productId) {
    planId = PLAN_ORDER[planId] || PLANS.PRO_U.id
  }

  if (planId) {
    dispatch(setShowPaymentModal({ showPaymentModal: true, upgradePlanId: planId, ...payload }))
  }
}

export const parseFilename = (filename, url) => {
  if (isCustomDomain(url)) {
    return 'document.pdf'
  }
  const lastDotIndex = filename.lastIndexOf('.')
  const name = lastDotIndex !== -1 ? filename.substring(0, lastDotIndex) : filename // Get the name without the last extension
  const ext = lastDotIndex !== -1 ? filename.substring(lastDotIndex) : '' // Get the last extension
  const sanitizedName = name.replace(/[-\s.]+/g, '-').replace(/['?>~!@%()"{}&#!<>:`+]+/g, '') // Sanitize the name

  return `${sanitizedName}${ext}` // Return the sanitized name with the last extension
}

export const canSubmitUpload = (userProfile) => {
  if (!userProfile) return false // Ensure userProfile exists

  if (userProfile.productId) {
    return true
  }

  const { uploadResetDate, uploadCount } = userProfile

  // If uploadCount or uploadResetDate does not exist, allow submission
  if (uploadCount === undefined || uploadResetDate === undefined) {
    return true
  }

  const resetDate = new Date(uploadResetDate)
  const now = new Date()

  // Allow submission if resetDate has passed or uploadCount is less than 3
  return now >= resetDate || uploadCount < 3
}

export const addUpgradeReason = (reason) => {
  localStorage.setItem('upgradeReason', reason)
}

export const getUpgradeReason = () => {
  const reason = localStorage.getItem('upgradeReason')
  return reason || 'N/A'
}

export const getFileFromIndexedDB = () => {
  return new Promise((resolve, reject) => {
    const dbRequest = indexedDB.open('PDFViewerDB', 1)

    dbRequest.onerror = (event) => {
      reject(new Error('Error opening database: ' + event.target.error))
    }

    dbRequest.onsuccess = (event) => {
      const db = event.target.result
      const transaction = db.transaction(['pdfs'], 'readonly')
      const store = transaction.objectStore('pdfs')
      const request = store.getAll()

      request.onerror = (event) => {
        reject(new Error('Error retrieving PDF from IndexedDB: ' + event.target.error))
      }

      request.onsuccess = (event) => {
        const records = event.target.result
        if (records && records.length > 0) {
          records.sort((a, b) => b.timestamp - a.timestamp)
          const latestRecord = records[0]

          const binaryString = atob(latestRecord.data)
          const bytes = new Uint8Array(binaryString.length)
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i)
          }

          const file = new File([bytes.buffer], latestRecord.id, {
            type: 'application/pdf',
            lastModified: new Date()
          })
          file.path = latestRecord.id

          resolve(file)
        } else {
          resolve(null)
        }
      }
    }
  })
}

export const isFileNameUnique = (existingFiles, newFile) => {
  if (!newFile || !existingFiles) return true
  const newFileName = newFile.name || ''
  if (!newFileName) return true
  return !existingFiles.some((file) => {
    if (!file) return false
    const existingName = file.name || file.path || file.s3FileName || ''
    return existingName.toLowerCase() === newFileName.toLowerCase()
  })
}

export const getCookie = (name) => {
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) return parts.pop().split(';').shift()
}

export const setCookie = (name, value, days) => {
  let expires = ''
  if (days) {
    const date = new Date()
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000)
    expires = `; expires=${date.toUTCString()}`
  }
  document.cookie = `${name}=${value || ''}${expires}; path=/`
}

export const getDomain = (siteRecord) => siteRecord.subdomain || siteRecord.link

export const getDirectFileLinkForSite = (s) => {
  if ((s?.isPresignedUpload || s?.linkStatus !== 'processing') && s?.linkStatus !== 'error') {
    if (s.type === FILE_TYPE_PDF) {
      return `https://${getDomain(s)}/${getSafeFileName(s.filename)}`
    } else {
      // legacy links
      return getDirectFileLink(`https://${getDomain(s)}`, s.filename, 'pdf', s.lastUpdated || s.created)
    }
  }
}

export function generateLinkName(file, existingSubdomain) {
  if (existingSubdomain) return existingSubdomain

  if (!file || !file.name) return ''

  const baseName = cleanFileName(file.name)

  if (!baseName) {
    return uuidv4().split('-')[0]
  }

  return baseName
}

export const isUserOnTinyPlan = (userProfile) => {
  if (!userProfile) return false

  const { productId } = userProfile
  const TINY_PLAN_ID = PLANS.TINY.id

  return !productId || productId === TINY_PLAN_ID
}

export const generateRandomSubdomain = () => {
  const numberDictionary = NumberDictionary.generate({ min: 1, max: 99 })
  return uniqueNamesGenerator({
    dictionaries: [colors, names, numberDictionary],
    separator: '-',
    style: 'lowerCase'
  })
}

export const getProjectsToArchive = (liveSites) => {
  if (!Array.isArray(liveSites)) return { toArchive: [], toRemove: [] }
  const freePlanSizeLimit = 3 * 1024 * 1024

  const previewSites = liveSites.filter((site) => site.inPreviewMode || site.linkStatus === 'processing')
  const realSites = liveSites.filter((site) => !site.inPreviewMode && site.linkStatus !== 'processing')

  const oversized = realSites.filter((site) => (site.size || 0) > freePlanSizeLimit)
  const customDomain = realSites.filter((site) => site.isCustomDomain)
  const restricted = realSites.filter(
    (site) =>
      site.passwordProtected ||
      site.enableEmailGate ||
      site.enableSSO ||
      site.type === 'application/zip' ||
      site.type === 'application/x-zip-compressed'
  )

  const remaining = realSites.filter(
    (site) =>
      !oversized.some((o) => o.subdomain === site.subdomain) &&
      !customDomain.some((c) => c.subdomain === site.subdomain) &&
      !restricted.some((r) => r.subdomain === site.subdomain)
  )
  const sorted = [...remaining].sort((a, b) => new Date(b.created || 0) - new Date(a.created || 0))
  const toKeep = sorted.slice(0, 1)

  const countExcess = sorted.filter((s) => !toKeep.some((k) => k.subdomain === s.subdomain))

  const toArchive = [...new Set([...oversized, ...customDomain, ...restricted, ...countExcess])]
  const toRemove = previewSites.length > 0 && realSites.length + previewSites.length > 1 ? previewSites : []

  return { toArchive, toRemove }
}

export const buildCancelMessage = (archivedSubdomains, removedSubdomains) => {
  let msg =
    '<p>Your account will be downgraded to the <strong>Free plan</strong> (1 project, 3 MB max file size, no custom domains).</p>'
  if (archivedSubdomains.length > 0) {
    msg += '<p>The following project(s) will be archived:</p>'
    msg += `<ul style="text-align:left; padding-left:20px; margin: 8px 0;">${archivedSubdomains.map((s) => `<li>${s}</li>`).join('')}</ul>`
    msg += '<p style="font-size:14px; color:#666;">You can restore archived projects by upgrading later.</p>'
  }
  if (removedSubdomains.length > 0) {
    msg += '<p>The following preview project(s) will be removed:</p>'
    msg += `<ul style="text-align:left; padding-left:20px; margin: 8px 0;">${removedSubdomains.map((s) => `<li>${s}</li>`).join('')}</ul>`
  }
  msg += '<p>Are you sure?</p>'
  return msg
}
